import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DatePeriod } from '../../../../../../features/date-time/date-picker/date-picker-calendar/use-date-picker-calendar';
import { useLang } from '../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { useToggle } from '../../../../../../hooks/use-toggle';
import { dayjsLocales } from '../../../../../../i18n/i18n';
import { StaffPath } from '../../../../../../routes/manager/manager-enums';
import { EmployeeType } from '../../../../../../store/api/employees-api';
import { ShiftType } from '../../../../../../store/api/shifts-api';
import { useEmployeesStore } from '../../../../../../store/use-employee-store';
import { useShiftsStore } from '../../../../../../store/use-shifts-store';
import { defaultShiftsPeriod, defaultShiftsRange } from '../shifts-helpers';

const periodToLabel: Record<DatePeriod, string> = {
  day: 'HH:mm',
  week: 'DD MMM',
  month: 'DD ddd',
  year: 'MMM',
};

const format = 'YYYY-MM-DD HH:mm';

export const useShiftsGrid = () => {
  const { t, getLocale, currentLang } = useLang();

  const { getQueryParams, addQueryParams } = useQueryParams();
  const { fetchShiftList, list: shifts, isLoading, uploadedShifts } = useShiftsStore();
  const location = useLocation().pathname;
  const isImport = location.includes(StaffPath.ShiftsImport);

  const params = getQueryParams();
  const period = (params.period || defaultShiftsPeriod) as DatePeriod;
  const range = params.range || defaultShiftsRange;
  const branch = params.branch;
  const tag = params.tag;

  useEffect(() => {
    if (isImport) return;

    const start_time = dayjs(range).startOf('day').format('YYYY-MM-DD');

    let end_time;
    if (period === 'week') {
      end_time = dayjs(start_time).add(7, 'day');
    } else if (period === 'month') {
      end_time = dayjs(start_time).add(1, 'month');
    } else {
      end_time = dayjs(start_time).add(1, 'day');
    }
    end_time = end_time.format('YYYY-MM-DD');
    fetchShiftList({
      is_by_company: true,
      n: 1000,
      since: 0,
      start_time,
      end_time,
      place_ids: branch ? branch.split(',') : undefined,
      tag_ids: tag ? tag.split(',') : undefined,
    });
  }, [period, range, branch, tag, isImport]);

  const cols: { date: string; isWeekend?: boolean }[] = useMemo(() => {
    let dates: string[] = [];
    if (period === 'week') {
      dates = Array.from({ length: 7 }, (_, i) => dayjs(range).add(i, 'day').format(format));
    }

    if (period === 'month') {
      const daysInMonth = dayjs(range).daysInMonth();
      dates = Array.from({ length: daysInMonth }, (_, i) =>
        dayjs(range).add(i, 'day').format(format),
      );
    }

    if (period === 'day') {
      dates = Array.from({ length: 24 }, (_, i) =>
        dayjs(range).startOf('day').add(i, 'hour').format(format),
      );
    }

    return dates.map((date) => ({
      date,
      isWeekend: dayjs(date).day() === 0 || dayjs(date).day() === 6,
    }));
  }, [period, range]);

  const [isOpenShiftModal, toggleOpenShiftModal] = useToggle();

  const handleOpenShiftModal = (
    shifts: ShiftType[],
    employee: Pick<EmployeeType, 'id' | 'name' | 'surname' | 'tags'>,
    date?: string,
  ) => {
    if (isImport) return;

    addQueryParams({
      shifts: JSON.stringify(shifts),
      employee: JSON.stringify(employee),
      date,
    });
    toggleOpenShiftModal();
  };

  const handleCloseShiftModal = () => {
    addQueryParams({
      shifts: undefined,
      date: undefined,
      employee: undefined,
    });
    toggleOpenShiftModal();
  };

  const { staff, fetchEmployees } = useEmployeesStore();

  useEffect(() => {
    fetchEmployees({ page: 1, page_size: 1000, is_by_company: true });
  }, []);

  const employeesByRoleWithShifts = useMemo(() => {
    if (!staff?.length) return {};
    const localShifts = isImport ? uploadedShifts : shifts;

    const result: Record<string, (EmployeeType & { shifts: ShiftType[] })[]> = {};
    let employees = staff;

    if (tag || branch) {
      const tags = tag ? tag.split(',') : [];
      const branches = branch ? branch.split(',') : [];
      employees = staff?.filter((item) => {
        const hasTag = tags.length ? item.tags.some((tag) => tags.includes(String(tag.id))) : true;
        const hasBranch = branches.length
          ? item.places?.some((place) => branches.includes(String(place.id)))
          : true;
        return hasTag && hasBranch;
      });
    }
    employees?.forEach((item) => {
      const employeeShifts = localShifts?.filter((shift) => shift.staff.id === item.id) || [];
      const employeeWithShifts = { ...item, shifts: employeeShifts };
      const roles = item.tags;
      if (!roles.length) {
        const noRole = 'Без должности';
        result[noRole] = result[noRole] || [];
        result[noRole].push(employeeWithShifts);
        return;
      }

      employeeWithShifts.tags.forEach((role) => {
        if (!result[role.value]) result[role.value] = [];
        result[role.value].push(employeeWithShifts);
      });
    });

    return result;
  }, [staff, shifts, uploadedShifts, tag, branch]);

  const getColLabel = (day: string) => {
    const localized = dayjs(day).locale(dayjsLocales[currentLang]);
    return localized.format(periodToLabel[period as DatePeriod]);
  };

  return {
    getLocale,
    t,
    cols,
    isOpenShiftModal,
    handleOpenShiftModal,
    handleCloseShiftModal,
    employeesByRoleWithShifts,
    period,
    getColLabel,
    isLoading,
    isImport,
  };
};
